// Real customer reviews only — added manually. Leave arrays EMPTY until you have
// genuine reviews (fabricated testimonials breach Australian Consumer Law).
// Tanner sends real reviews; paste them in below with the customer's permission.
// The Reviews section hides itself automatically while an array is empty.
//
// Shape: { name, location, rating (1-5), date: "YYYY-MM-DD", text, verified,
//          product, variant, covers }
// `product` is what they actually bought — set it only when we know, never as a
// default, so the line under a review is always substantiable. `variant` is the
// option they picked (Judge.me reports it as "Item type").
// `covers` lists the product page(s) the review belongs to — "supported",
// "flat", "galvanised-extension" (or "aluminium-extension"). A product page only
// shows, counts and sends to Google the reviews that cover it, so a review of
// one product is never published as a rating of another. Every entry needs it.
// `photo` (optional) is { src, alt } — a photo the customer sent us of their
// own job, shown on their review card. Never a stock or staged shot.
// Example:
// { name: "Dave M.", location: "Bendigo, VIC", rating: 5, date: "2026-05-02",
//   text: "Screwed timber rails straight onto my pickets in minutes.", verified: true,
//   product: "Star Picket Bracket" }

// Every published review, store-wide. The home page and /reviews show all of
// them; each product page picks out its own with reviewsFor() below.
export const REVIEWS = [
  // Verified Judge.me product review (submitted via post-order email, published on Judge.me).
  {
    name: "Michael Nelson",
    rating: 5,
    date: "2026-07-04",
    text: "These are ideal for holding boxing in place when doing a concreting job.",
    verified: true,
    source: "via Judge.me",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  {
    // Verified purchaser — supported bracket, 2.5 mm variant.
    // Collected by Loox, not Judge.me (confirmed against the Shopify loox
    // metafield 2026-09-09). Both apps are installed and each holds its own
    // separate reviews, so check which one a review actually came from.
    name: "G M.",
    rating: 5,
    date: "2026-08-29",
    text: "Easy to use and can reuse if needed.",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
    variant: "2.5 mm",
  },
  {
    // Verified purchaser — collected by Loox (Shopify loox metafield,
    // confirmed 2026-09-09). Variant confirmed by Tanner 2026-09-04.
    name: "Naveen C.",
    rating: 5,
    date: "2026-09-03",
    text: "Perfect! Great fit to pickets 🤗",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
    variant: "2.5 mm",
  },
  {
    // Ivor left TWO 5-star reviews through Loox on 2026-09-13: a product review
    // of the Star Picket Brackets ("Excellent solution for garden building
    // projects.") and a store review ("Product arrived on time and as
    // described."). Both are his own words, same purchase, same day. They are
    // held as ONE entry on Tanner's call, so one customer counts once in the
    // published count and the aggregate.
    name: "Ivor R.",
    rating: 5,
    date: "2026-09-13",
    text: "Excellent solution for garden building projects. Product arrived on time and as described.",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  {
    // Verified purchaser — order #1097, 20 x Flat Slide-On Bracket. Collected
    // by Loox, published 2026-09-17.
    name: "David F.",
    rating: 5,
    date: "2026-09-17",
    text: "They fit perfectly. Thank you.",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Verified purchaser — order #1094, 20 x Galvanised Fence Height Extension,
    // 600 mm. Collected by Loox, published 2026-09-17. Word for word, as written.
    name: "Andrew L.",
    rating: 5,
    date: "2026-09-17",
    text: "They look good, and are a firm fit to the star pickets, haven't installed them yet still preparing the existing fence for the extensions, brought them because I only needed a few and could not buy the long pickets in a low volume",
    verified: true,
    source: "via Loox",
    product: "Galvanised Fence Height Extension",
    variant: "600 mm",
    covers: ["galvanised-extension"],
  },
  {
    // Verified purchaser — order #1102: 3 x Star Picket Bracket (2.5 mm) and
    // 12 x Flat Slide-On Bracket, plus installation kits. Steve didn't use the
    // Loox form; he replied to the Loox "how did it go?" email on 2026-09-18
    // with "5 stars guys" and a corner bracket suggestion. Only the rating is
    // published: the suggestion is feedback for Tanner, not review copy.
    // It rates the whole order, so it counts once in the store-wide figures and
    // once on each bracket page he bought from.
    name: "Steve R.",
    rating: 5,
    date: "2026-09-18",
    text: "5 stars.",
    verified: true,
    source: "via email",
    product: "Star Picket Bracket + Flat Slide-On Bracket",
    job: "Small market garden",
    covers: ["supported", "flat"],
    // Steve's own photo of the job (sent 2026-09-18). Tanner confirmed the
    // corners use a mix of both brackets, so it's in both bracket galleries too.
    photo: {
      src: "/images/customer-steve-r-garden-frames.jpg",
      alt: "Steve R.'s small market garden: timber bed and path frames held to star pickets at the corners with StakeTek supported and flat slide-on brackets",
      // Panoramic: cover would cut the corner brackets off both ends.
      fit: "contain",
    },
  },
  {
    // Verified purchaser — collected by Loox, published 2026-09-23 (Loox review
    // h7t_stt8P). A DIFFERENT Michael to the Michael Nelson at the top of this
    // list, who came through Judge.me in July. Two people, two reviews.
    // Written as four short lines in Loox and joined here, wording untouched,
    // including their spelling of "Stake Tek".
    name: "Michael B.",
    rating: 5,
    date: "2026-09-23",
    text: "Very good quality. Easy to use. Do exactly what they are meant to. Well done Stake Tek",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  {
    // Verified purchaser — collected by Loox, published 2026-09-28 (Loox review
    // WIO_bHK73). First rated review to land on the flat bracket from someone
    // other than David F. and Steve R.
    name: "Graham B.",
    rating: 5,
    date: "2026-09-28",
    text: "Are very good fit for purpose",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Michael B.'s SECOND review, this one of the flat bracket (Loox _YIKQAwhm,
    // 2026-09-28). Same person as the Star Picket Bracket entry above: two
    // separate products, two separate reviews, so he is held as two entries and
    // counts on each product page, the way Loox itself counts him.
    // He wrote the two in nearly the same words. Published here without his
    // closing "Well done Stake Tek.", which was verbatim the same line as his
    // other review, so the two read less like a duplicate. What is left is
    // still his own wording, untouched. Nothing rewritten: inventing review
    // copy would breach the ACL rule at the top of this file.
    name: "Michael B.",
    rating: 5,
    date: "2026-09-28",
    text: "Good quality product. Does exactly what it is supposed to.",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Sue left TWO 5-star reviews through Loox on 2026-09-28: a product review
    // of the Flat Slide-On Bracket (ZKoxAHHbV) and a store review of the
    // checkout ("Prompt response"). Both her own words, same purchase, same
    // day, held as ONE entry on Tanner's call so one customer counts once.
    // Same handling as Ivor R. above. Only change is her lowercase "i"
    // capitalised, as was done for G M.
    name: "Sue K.",
    rating: 5,
    date: "2026-09-28",
    text: "Those brackets made the job so much easier and neater in the garden. I wish I had found this product many years ago. Great work. Prompt response.",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Verified purchaser — collected by Loox, published 2026-09-29
    // (Loox review WBy0Cgk6M).
    name: "David S.",
    rating: 5,
    date: "2026-09-29",
    text: "Easy and very effective!",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  {
    // Verified purchaser, order #1133 (4 x Flat Slide-On Bracket). Col left TWO
    // 5-star Loox reviews of the flat bracket on 2026-10-01 ("Great product",
    // GLknlk3P2Y, and BoFEWM37C below it). Same customer, same order, so held
    // as ONE entry, same handling as Sue K. Both his own words, joined.
    name: "Col D.",
    rating: 5,
    date: "2026-10-01",
    text: "Great product. Looks good but haven’t used it yet.",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Verified purchaser, order #1129: 30 x Star Picket Bracket (2.5 mm) plus a
    // 30-bracket installation kit. Loox filed it under the Installation Kit
    // (uecx6bGuC, 2026-10-02); the brackets he bought are the supported ones,
    // so it covers that page. He also left a Loox store review ("Easy ordering
    // process. Good delivery time"); joined into this one entry on Tanner's
    // call so one customer counts once. Only change: double spaces collapsed
    // and full stops added where the two pieces join.
    name: "Mark D.",
    rating: 5,
    date: "2026-10-02",
    text: "Great product. So many applications walls and gardens. Form work. Easy ordering process. Good delivery time.",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
    variant: "2.5 mm",
  },
  {
    // Verified purchaser, order #1148: 10 x Flat Slide-On Bracket plus a
    // 10-bracket installation kit. Loox filed it under the Installation Kit
    // (t0_ekYhwC, 2026-10-05); the brackets are flat ones, so it covers the
    // flat page. Only change is his lowercase "i" capitalised, as for Sue K.
    name: "William E.",
    rating: 5,
    date: "2026-10-05",
    text: "Haven't yet deployed them. So no photo. The 5 stars I gave them are for product quality. They look sturdy and fully capable if holding down my shed when it’s erected.",
    verified: true,
    source: "via Loox",
    product: "Flat Slide-On Bracket",
    covers: ["flat"],
  },
  {
    // Verified purchaser, order #1151: 20 x Star Picket Bracket (2.5 mm) plus a
    // 20-bracket installation kit. Loox filed it under the Installation Kit
    // (UIY6xrpkZ, 2026-10-06); covers the supported page. Verbatim.
    name: "Sandi H.",
    rating: 5,
    date: "2026-10-06",
    text: "Great service and product. Cant wait to use them :)",
    verified: true,
    source: "via Loox",
    product: "Star Picket Bracket",
    covers: ["supported"],
    variant: "2.5 mm",
  },
  // Genuine customer reviews.
  {
    name: "Kerry",
    rating: 5,
    source: "Facebook",
    text: "It's what everyone with a star picket needs!",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  {
    // Rod gave a 5-star rating without leaving a written comment. Confirmed by
    // Tanner 2026-08-29 as a verified purchaser.
    name: "Rod",
    rating: 5,
    verified: true,
    source: "Facebook",
    product: "Star Picket Bracket",
    covers: ["supported"],
  },
  // Paul Murdica (local landscaper) — PENDING his permission + a quote in his own words.
];

// The single line printed under a reviewer's name, e.g.
//   "Verified purchase · Star Picket Bracket, 2.5 mm"
//   "Facebook · Star Picket Bracket"
// Each part is dropped when we don't hold it, so the line never claims a
// purchase or a product we can't back up.
// Stable React list key. The name alone is NOT unique: one customer can review
// more than one product and gets an entry for each (Michael B. has one for the
// supported bracket and one for the flat). Two children with the same key make
// React drop or duplicate one of them.
export const reviewKey = (r) =>
  `${r.name}|${r.date || r.source || ""}|${(r.covers || []).join("+")}`;

export function reviewMetaLine(r) {
  if (!r) return "";
  // `job` is what they built with it, when we know: that tells a buyer what the
  // brackets are for better than the part names do.
  const bought = r.product ? (r.variant ? `${r.product}, ${r.variant}` : r.product) : null;
  return [r.verified ? "Verified purchase" : r.source, bought, r.job].filter(Boolean).join(" · ");
}

export const PICKET_REVIEWS = [];

export const HURDLE_REVIEWS = [];

// Headline average + schema are built ONLY from entries that carry a real numeric
// rating. Ratingless comment testimonials are ignored here so we never publish a
// fabricated star score.
export function aggregate(reviews) {
  if (!reviews) return null;
  const rated = reviews.filter((r) => Number(r.rating) > 0);
  if (rated.length === 0) return null;
  const sum = rated.reduce((a, r) => a + Number(r.rating), 0);
  return {
    count: rated.length,
    average: Math.round((sum / rated.length) * 10) / 10,
    // Only claim "verified reviews" when every counted review really is one.
    // Facebook comments from people we can't tie to an order are still real
    // reviews, but they are not verified purchases.
    allVerified: rated.every((r) => r.verified === true),
  };
}

// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH for every published review figure.
//
// Derived from REVIEWS above, so the count shown on the site and the
// reviewCount published to Google in Product schema can never drift apart —
// they are the same number by construction. Only reviews we hold in this file
// are ever counted, so every published figure is substantiable.
//
// Two levels:
//   REVIEW_SUMMARY          store-wide — home page, /reviews, /products.
//   reviewSummaryFor(key)   one product page — its title line, its review
//                           cards and its Product schema all use this, so a
//                           page never shows or sends Google a rating that
//                           includes reviews of a different product.
//
// `source` is intentionally omitted: the reviews come from more than one place
// (Loox, Judge.me, Facebook, email), so a single-source claim on the aggregate
// would be inaccurate. Each review still carries and displays its own `source`.
// ---------------------------------------------------------------------------
export const REVIEW_SUMMARY = aggregate(REVIEWS);

// Reviews that cover a product page. `keys` is one key or a list of them; a
// review that covers several of the keys is still returned once, so the
// brackets hub (["supported", "flat"]) counts Steve R. once, not twice.
export function reviewsFor(keys) {
  const want = [].concat(keys);
  return REVIEWS.filter((r) => (r.covers || []).some((k) => want.includes(k)));
}

export function reviewSummaryFor(keys) {
  return aggregate(reviewsFor(keys));
}

// ---------------------------------------------------------------------------
// LIVE JUDGE.ME STATS — CONNECT POINT (not published anywhere yet).
// Judge.me is installed on the Shopify store and collects verified reviews
// after checkout. This is deliberately NOT wired into REVIEW_SUMMARY or Product
// schema: publishing a separate Judge.me-only count is what previously caused
// the site to display one number while sending a different one to Google.
// To go live, either:
//   1. Paste each new Judge.me review into REVIEWS above (it then flows
//      into the visible count and the schema together), or
//   2. Wire the Judge.me API via a small fetch — see the notes in
//      components/JudgeMeReviews.jsx for endpoint + token details.
// ---------------------------------------------------------------------------
export const JUDGEME_STATS = {
  average: 5.0, // Judge.me dashboard aggregate — Star Picket Brackets (updated 2026-09-04)
  count: 3,     // verified product reviews on Judge.me
};
